import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faPaperclip } from "@fortawesome/free-solid-svg-icons";
import {
  getTasksByEmployeeAndDate,
  getCurrentApprover,
  approveReport,
  rejectReport,
  cloneTask,
} from "../../../../../api/hrm/tasks/task_info";
import { workDayToApiParam } from "../../../../../utils/workDayApiParam";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../../../components/ConfirmDialog";
import {
  TaskPopupFrame,
  PopupSectionCard,
  PopupActionButton,
  PopupStatusBadge,
  PopupEmployeeInfoCard,
  PopupImagePreview,
  resolveTaskTypeLabel,
} from "./PopupUiKit";

export default function TaskPopupReported({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  branchName,
  workDay,
  taskList = [],
  onUpdated,
}) {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const formattedDate = dayjs(workDay).isValid()
    ? dayjs(workDay).format("DD/MM/YYYY")
    : "—";

  const [reports, setReports] = useState([]);
  const [approvableTasks, setApprovableTasks] = useState({});
  const [approverLoaded, setApproverLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingIndex, setSendingIndex] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // 🧠 Lấy danh sách báo cáo + kiểm tra quyền duyệt (gộp chung)
  const fetchReports = useCallback(async () => {
    if (!employeeId || !workDay) return;
    try {
      setLoading(true);
      setApproverLoaded(false);

      const dayParam = workDayToApiParam(workDay);
      const data = await getTasksByEmployeeAndDate(employeeId, dayParam, "reported");
      const list =
        data?.items?.map((t) => ({
          id: t.id,
          taskname: t.taskname,
          note: t.note || "",
          reports: t.reports || [],
          status: t.status || "waiting",
          attachment_url: t.attachment_url,
          attachment_mime_type: t.attachment_mime_type,
          score: 0,
          comment: "",
          work_date: t.work_date,
          employee_id: t.employee_id,
          branch_id: t.branch_id,
          department_id: t.department_id,
          task_type: t.task_type,
          assigner_name: t.assigner_name,   // 👈 thêm dòng này
          assigned_at: t.assigned_at,
          work_time: t.work_time,
        })) || [];

      setReports(list);

      // ✅ Kiểm tra quyền duyệt ngay tại đây (tránh vòng lặp useEffect)
      const approverResults = {};
      for (const t of list) {
        if (!t?.id) continue;
        try {
          const res = await getCurrentApprover(t.id);
          const approvers = res?.approvers || [];
          const isCurrent = approvers.some((a) => a.id === currentUser.id);
          approverResults[t.id] = isCurrent;
        } catch {
          approverResults[t.id] = false;
        }
      }
      setApprovableTasks(approverResults);
      setApproverLoaded(true);
      return { list, approverResults };
    } catch (err) {
      console.error("❌ Lỗi tải báo cáo:", err);
      showErrorToast("Không thể tải danh sách báo cáo công việc.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [employeeId, workDay, currentUser.id]);

  const closeIfNoTasksLeftToApprove = useCallback((list, approverResults) => {
    if (!list?.length) {
      onClose?.();
      return;
    }
    const stillApprovable = list.some((t) => t?.id && approverResults[t.id]);
    if (!stillApprovable) onClose?.();
  }, [onClose]);

  // 🧩 Khi mở popup → chỉ gọi 1 lần
  useEffect(() => {
    if (isOpen && employeeId && workDay) {
      fetchReports();
    }
  }, [isOpen, employeeId, workDay, fetchReports]);

  // Nếu chuỗi từ BE dạng "2025-11-17 13:58:00.123456"
  const formatAssignedAt = (value) => {
    if (!value) return "";

    // 1️⃣ Nếu là Date object -> format luôn
    if (value instanceof Date) {
      return dayjs(value).format("HH:mm DD-MM-YYYY");
    }

    let str = String(value);

    // 2️⃣ Chuẩn hoá: "2025-11-17 13:58:00" -> "2025-11-17T13:58:00"
    if (str.includes(" ") && !str.includes("T")) {
      const [datePart, timePart] = str.split(" ");
      // cắt bớt phần mili giây nếu có
      const cleanTime = timePart.split(".")[0];
      str = `${datePart}T${cleanTime}`;
    }

    const d = dayjs(str);
    if (!d.isValid()) return "";

    // 3️⃣ Hiển thị giờ VN (giả sử máy client đang ở VN, không cần cộng +7 nữa)
    return d.format("HH:mm DD-MM-YYYY");
  };

  const formatWorkTime = (t) => {
    if (!t) return "";
    return t.substring(0, 5); // lấy HH:mm
  };

  // ⭐ Chấm điểm
  const handleSetScore = (index, value) => {
    setReports((prev) =>
      prev.map((r, i) => (i === index ? { ...r, score: value } : r))
    );
  };

  // 💬 Nhập nhận xét
  const handleChangeComment = (index, value) => {
    setReports((prev) =>
      prev.map((r, i) => (i === index ? { ...r, comment: value } : r))
    );
  };

  // ✅ Duyệt báo cáo
  const handleApprove = async (index) => {
    const r = reports[index];
    if (!r.score) {
      showWarningToast("Vui lòng chấm điểm trước khi duyệt.");
      return;
    }
    try {
      setSendingIndex(index);
      const res = await approveReport(r.id, {
        judged_by: currentUser.id,
        judge_point: r.score,
        judge_content: r.comment || "",
      });
      showSuccessToast(res.message || "Đã duyệt báo cáo công việc!");
      const refreshed = await fetchReports();
      if (refreshed) closeIfNoTasksLeftToApprove(refreshed.list, refreshed.approverResults);
      onUpdated?.();
    } catch (err) {
      console.error("❌ Lỗi duyệt báo cáo:", err);
      showErrorToast("Không thể duyệt báo cáo công việc.");
    } finally {
      setSendingIndex(null);
    }
  };

  // ❌ Không duyệt báo cáo
  const handleReject = async (index) => {
    try {
      setSendingIndex(index);
      const r = reports[index];
      if (!r.comment || r.comment.trim() === "") {
        showErrorToast("Vui lòng nhập lý do không duyệt trước khi lưu!");
        return;
      }

      const res = await rejectReport(r.id, {
        judged_by: currentUser.id,
        judge_content: r.comment.trim(),
        judge_point: r.score || null,
      });

      showSuccessToast(res.message || "Đã không duyệt báo cáo công việc!");
      const refreshed = await fetchReports();
      if (refreshed) closeIfNoTasksLeftToApprove(refreshed.list, refreshed.approverResults);
      onUpdated?.();
    } catch (err) {
      console.error("❌ Lỗi khi không duyệt:", err);
      showErrorToast("Không thể cập nhật hoặc tạo lại công việc!");
    } finally {
      setSendingIndex(null);
    }
  };

  if (!employeeId) return null;

  const typeFromLoaded = resolveTaskTypeLabel(reports);
  const typeFromCard = resolveTaskTypeLabel(taskList);
  const taskTypeLabel =
    typeFromLoaded !== "—" ? typeFromLoaded : typeFromCard;

  return (
    <>
      <TaskPopupFrame
        isOpen={isOpen}
        onClose={onClose}
        maxWidthClass="max-w-5xl"
        titleIcon={
          <FontAwesomeIcon icon={faStar} className="h-5 w-5 text-red-600 sm:h-6 sm:w-6" />
        }
        title="Đánh giá"
        titleHighlight="báo cáo"
        subtitle="Chấm điểm và phê duyệt báo cáo công việc"
      >
        <PopupEmployeeInfoCard
          branchName={branchName || reports?.[0]?.branch_name || "—"}
          employeeName={employeeName}
          workDay={formattedDate}
          taskTypeLabel={taskTypeLabel}
        />

                {loading ? (
                  <p className="text-gray-500 italic text-sm bg-white rounded-2xl border border-gray-300 p-4">Đang tải báo cáo...</p>
                ) : reports.length === 0 ? (
                  <p className="text-gray-500 italic text-sm bg-white rounded-2xl border border-gray-300 p-4">
                    Không có báo cáo công việc nào.
                  </p>
                ) : (
                  reports.map((r, i) => (
                    <PopupSectionCard
                      key={i}
                      className={`${
                        r.status === "approved"
                          ? "border-emerald-200"
                          : r.status === "rejected"
                          ? "border-red-200"
                          : "border-gray-300"
                      }`}
                    >
                      {/* Header */}
                      <div className="flex justify-between items-start mb-2">
                        {/* Bên trái: Tên, ghi chú, người giao */}
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">
                            {i + 1}. {r.taskname}
                          </div>

                          {r.note && (
                            <div className="text-xs text-gray-500 italic mt-1">
                              {r.note}
                            </div>
                          )}
                          
                          {/* File đính kèm gốc từ người giao việc */}
                          {r.attachment_url && (
                            <button
                              onClick={() => window.open(r.attachment_url, "_blank")}
                              className="
                                flex items-center gap-2
                                bg-gray-100 hover:bg-gray-200
                                text-gray-700 text-[11px] font-bold uppercase tracking-wide
                                text-xs px-3 py-1.5 rounded-xl border border-gray-200 transition
                              "
                              title="Mở file đính kèm"
                            >
                              <FontAwesomeIcon
                                icon={faPaperclip}
                                className="text-gray-500 text-sm"
                              />
                              <span>File giao việc</span>
                            </button>
                          )}

                          {r.assigner_name && (
                            <div className="text-xs text-red-500 italic mb-1">
                              Được giao bởi: {r.assigner_name}
                              {r.assigned_at && (
                                <span className="ml-1">
                                  ({formatAssignedAt(r.assigned_at)})
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Bên phải: Hai tag xếp dọc */}
                        <div className="flex flex-col items-end gap-1 ml-3">
                          {/* Trạng thái duyệt */}
                          <PopupStatusBadge status={r.status} />

                          {/* Tag “Việc được giao” */}
                          {r.task_type === "assigned" && (
                            <span className="text-[10px] bg-red-50 text-red-700 px-2.5 py-1 rounded-full border border-red-200 font-bold uppercase tracking-wider shadow-sm">
                              Yêu cầu trước {formatWorkTime(r.work_time)}
                            </span>
                          )}
                        </div>
                      </div>

                        {/* Báo cáo chi tiết */}
                        {r.reports?.map((rep, idx) => (
                        <div key={idx} className="mt-2 border-t pt-2">
                            {/* Nội dung báo cáo */}
                            <p className="text-sm text-blue-800 mb-2">
                            📜 <strong>{rep.report_content || "(Không có nội dung báo cáo)"}</strong>
                            </p>

                            {/* Danh sách file (pdf hoặc ảnh) */}
                            {rep.files && rep.files.length > 0 && (
                            <div className="space-y-3 mt-2">

                                {rep.files.map((f, fIndex) => {
                                const mime = f.mime_type || f.file_type || "";
                                const isImage = mime.startsWith("image/");
                                const isPDF = mime === "application/pdf";
                                const hasGPS =
                                    f.location &&
                                    typeof f.location.lat === "number" &&
                                    typeof f.location.lng === "number";

                                /* 1️⃣ Ảnh chụp từ điện thoại → có GPS */
                                if (isImage && hasGPS) {
                                    return (
                                    <div
                                        key={fIndex}
                                        className="border rounded-lg p-3 bg-white shadow-sm"
                                    >
                                        <img
                                        src={f.file_url}
                                        alt="Ảnh báo cáo"
                                        onClick={() => setPreviewImage(f.file_url)}
                                        className="w-40 h-40 object-cover rounded cursor-pointer hover:scale-105 transition"
                                        />

                                        <button
                                        onClick={() =>
                                            window.open(
                                            `https://maps.google.com/?q=${f.location.lat},${f.location.lng}`,
                                            "_blank"
                                            )
                                        }
                                        className="mt-2 text-sm text-blue-600 underline"
                                        >
                                        📍 Xem vị trí
                                        </button>
                                    </div>
                                    );
                                }

                                /* 2️⃣ PDF */
                                if (isPDF) {
                                    return (
                                    <div
                                        key={fIndex}
                                        className="border rounded-lg p-3 bg-white shadow-sm"
                                    >
                                        <a
                                        href={f.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-700 text-sm font-medium"
                                        >
                                        📄 Xem file PDF
                                        </a>
                                    </div>
                                    );
                                }

                                /* 3️⃣ Ảnh đính kèm bình thường → hiển thị như PDF */
                                if (isImage && !hasGPS) {
                                    return (
                                    <div
                                        key={fIndex}
                                        className="border rounded-lg p-3 bg-white shadow-sm"
                                    >
                                        <a
                                        href={f.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-700 text-sm font-medium"
                                        >
                                        🖼️ Xem ảnh đính kèm
                                        </a>
                                    </div>
                                    );
                                }

                                return null;
                                })}

                            </div>
                            )}
                        </div>
                        ))}


                        {/* 💬 Kiểm tra quyền duyệt */}
                        {approverLoaded && approvableTasks[r.id] && (
                        <>
                            <textarea
                            value={r.comment}
                            onChange={(e) => handleChangeComment(i, e.target.value)}
                            rows={2}
                            placeholder="Nhập nhận xét."
                            className="w-full border border-gray-200 rounded-xl p-3 text-sm mt-3 mb-2 focus:ring-4 focus:ring-red-50 focus:border-red-300"
                            />

                            <div className="flex items-center gap-2 mb-3">
                            <span className="text-sm font-medium text-gray-700">
                                Chấm điểm:
                            </span>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <FontAwesomeIcon
                                key={s}
                                icon={faStar}
                                onClick={() => handleSetScore(i, s)}
                                className={`cursor-pointer text-lg ${
                                    s <= r.score ? "text-yellow-400" : "text-gray-300"
                                }`}
                                />
                            ))}
                            <span className="text-sm text-gray-500 ml-2">
                                {r.score}/5
                            </span>
                            </div>

                            <div className="flex justify-end mt-4 gap-3">
                            <PopupActionButton
                                onClick={() => handleReject(i)}
                                disabled={sendingIndex === i}
                                variant="danger-outline"
                                className={
                                  sendingIndex === i
                                    ? "bg-red-100 text-red-400 cursor-not-allowed opacity-60 hover:bg-red-100"
                                    : ""
                                }
                            >
                                Không duyệt
                            </PopupActionButton>

                            <PopupActionButton
                                onClick={() => handleApprove(i)}
                                disabled={sendingIndex === i}
                                className={
                                  sendingIndex === i
                                    ? "bg-red-200 text-red-300 cursor-not-allowed opacity-60 hover:bg-red-200"
                                    : ""
                                }
                            >
                                Duyệt
                            </PopupActionButton>
                            </div>
                        </>
                        )}

                    </PopupSectionCard>
                  ))
                )}
      </TaskPopupFrame>

      <PopupImagePreview src={previewImage} onClose={() => setPreviewImage(null)} />
    </>
  );
}
